import LoadingIcon from '@vuejs/components/shared/LoadingIcon'
import {
	DELAY,
	PER_PAGE,
} from '@vuejs/helpers/_constants'
import {
	computed,
	defineComponent,
	nextTick,
	onMounted,
	onUnmounted,
	type PropType,
	ref,
	watch,
} from 'vue'

import CAIconComponent from '../shared/CAIconComponent'
import CATableCell from './CATableCell'
import CATableRow from './CATableRow'

/**
 * Table component
 *
 * @component CATable
 *
 * @prop {boolean} [backendSort=false] - Whether to use backend sorting instead of frontend sorting.
 * @prop {Array<string>} [exclude=[]] - Array of table headers and/or rows to exclude from the table.
 * @prop {Array<string>} [headers=[]] - Array of table headers, whatever you pass in here is what shows up from left to right.
 * @prop {Array<string>} [headersToSort=[]] - Array of table headers to sort, if not provided all headers are sortable.
 * @prop {boolean} [infiniteScroll=false] - Whether to enable infinite scroll when there are more than 20 results.
 * @prop {number} [infiniteScrollThreshold=20] - Number of items to show initially and load per batch. Defaults to 20.
 * @prop {boolean} [isStickyHeader=false] - Whether the table header should be sticky or not.
 * @prop {boolean} [sortable=false] - Whether the table columns should be sortable when headers are clicked.
 * @prop {Array<any>} [sortableData=[]] - Data array to use for sorting when using slots for complex rendering.
 * @prop {Function} [sortFunction=null] - Custom sort function that receives (data, column, direction) and returns sorted data.
 * @prop {Array<CATableRowItem>} [tableRows=[]] - Array of table rows, whatever you pass in here is what shows up from top to bottom.
 *
 * @slot customTableHeader - Custom table header slot
 * @slot tableFooter - Table footer slot
 * @slot tableHeader - Table header slot
 * @slot tableRows - Table rows slot
 *
 * @emit sort - Emitted when sort changes with { column, direction, sortedData }
 */

export interface CATableRowItem {
	[key: string]: string
}

export default defineComponent({
	name: 'CATable',

	emits: {
		sort: (_payload: {
			column: string
			direction: 'ascending' | 'descending'
			sortedData: any[]
		}) => true,
	},

	props: {
		/**
		 * Whether to use backend sorting instead of frontend sorting.
		 * @default false
		 * @type {boolean}
		 */
		backendSort: { default: false, type: Boolean },
		/**
		 * Initial column to sort by (useful for persisting sort state from URL).
		 * @default ''
		 * @type {string}
		 */
		initialSortColumn: {
			default: '',
			optional: true,
			type: String,
		},
		/**
		 * Initial sort direction (useful for persisting sort state from URL).
		 * @default 'ascending'
		 * @type {'ascending' | 'descending'}
		 */
		initialSortDirection: {
			default: 'ascending',
			optional: true,
			type: String as PropType<'ascending' | 'descending'>,
		},
		/**
		 *
		 * Template available if preferred. Array of table headers and/or rows to exclude from the table.
		 *
		 * @default []
		 * @type {Array<string>}
		 */
		exclude: { optional: true, type: Array as PropType<string[]> },
		/**
		 * Template available if preferred. Array of table headers, whatever you pass in here is what shows up from left to right.
		 * @default []
		 * @type {Array<string>}
		 */
		headers: { optional: true, type: Array as PropType<string[]> },
		/**
		 * Template available if preferred. Array of table headers to sort, if not provided all headers are sortable.
		 * @default []
		 * @type {Array<string>}
		 */
		headersToSort: { optional: true, type: Array as PropType<string[]> },
		/**
		 * Whether to enable infinite scroll when there are more than 20 results.
		 * @default false
		 * @type {boolean}
		 */
		infiniteScroll: { default: false, type: Boolean },
		/**
		 * Number of items to show initially and load per batch. Defaults to 20.
		 * @default 20
		 * @type {number}
		 */
		infiniteScrollThreshold: { default: PER_PAGE.ITEMS, type: Number },
		/**
		 * Whether the table header should be sticky or not.
		 * @default false
		 * @type {boolean}
		 */
		isStickyHeader: { default: false, type: Boolean },
		/**
		 * Whether the table columns should be sortable when headers are clicked.
		 * @default false
		 * @type {boolean}
		 */
		sortable: { default: false, type: Boolean },
		/**
		 * Template available if preferred. Data array to use for sorting when using slots for complex rendering.
		 * @default []
		 * @type {Array<any>}
		 */
		sortableData: { optional: true, type: Array as PropType<any[]> },
		/**
		 * Custom sort function that receives (data, column, direction) and returns sorted data.
		 * @default null
		 * @type {Function}
		 */
		sortFunction: { optional: true, type: Function as PropType<(data: any[], column: string, direction: 'ascending' | 'descending') => any[]> },
		/**
		 *
		 * Template available if preferred. Array of table rows, whatever you pass in here is what shows up from top to bottom.
		 * @default []
		 * @type {Array<CATableRowItem>}
		 * @typedef {Object} CATableRowItem
		 * @prop {string} [key: string] - This is going to serve as a variable to hold the value of the row item.
		 */
		tableRows: { optional: true, type: Array as PropType<CATableRowItem[]> },
	},

	setup(props, { slots, emit }) {
		const displayedRowsCount = ref(props.infiniteScrollThreshold)
		const intersectionObserver = ref<IntersectionObserver | null>(null)
		const loadingMoreRows = ref(false)
		const sentinelRef = ref<HTMLElement | null>(null)
		const sortColumn = ref<string | null>(props.initialSortColumn || null)
		const sortDirection = ref<'ascending' | 'descending'>(props.initialSortDirection || 'ascending')

		const computedExcludeList = computed(() => {
			return props.exclude?.map(header => header.toLowerCase()) ?? []
		})

		const sortData = (data: any[], column: string, direction: 'ascending' | 'descending') => {
			if (!data || !column || props.backendSort) {
				return data
			}

			const sorted = [...data].sort((a, b) => {
				const aVal = a[column]
				const bVal = b[column]

				if (aVal === null && bVal === null) return 0
				if (aVal === null) return 1
				if (bVal === null) return -1

				const aNum = parseFloat(aVal)
				const bNum = parseFloat(bVal)

				if (!isNaN(aNum) && !isNaN(bNum)) {
					return aNum - bNum
				}

				const aDate = new Date(aVal)
				const bDate = new Date(bVal)

				if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
					return aDate.getTime() - bDate.getTime()
				}

				return String(aVal).localeCompare(String(bVal))
			})

			return direction === 'descending' ? sorted.reverse() : sorted
		}

		const sortedTableRows = computed(() => {
			if (!props.tableRows || !props.sortable || !sortColumn.value) {
				return props.tableRows || []
			}

			return sortData(props.tableRows, sortColumn.value, sortDirection.value)
		})

		const sortedSlotData = computed(() => {
			return props.sortableData || props.tableRows || []
		})

		const displayedTableRows = computed(() => {
			const allRows = sortedTableRows.value

			if (!props.infiniteScroll || allRows.length <= props.infiniteScrollThreshold) {
				return allRows
			}

			return allRows.slice(0, displayedRowsCount.value)
		})

		const displayedSlotRows = computed(() => {
			const allRows = sortedSlotData.value

			if (!props.infiniteScroll || allRows.length <= props.infiniteScrollThreshold) {
				return allRows
			}

			return allRows.slice(0, displayedRowsCount.value)
		})

		const hasMoreRows = computed(() => {
			const allRows = props.tableRows ? sortedTableRows.value : sortedSlotData.value

			return props.infiniteScroll && allRows.length > displayedRowsCount.value
		})

		const shouldShowInfiniteScroll = computed(() => {
			const allRows = props.tableRows ? sortedTableRows.value : sortedSlotData.value

			return props.infiniteScroll && allRows.length > props.infiniteScrollThreshold
		})

		const isColumnSortable = (header: string) => {
			if (!props.sortable) return false

			return props.headersToSort ? props.headersToSort.includes(header) : true
		}

		const handleSort = (header: string) => {
			if (!isColumnSortable(header)) {
				return
			}

			if (sortColumn.value === header) {
				sortDirection.value = sortDirection.value === 'ascending' ? 'descending' : 'ascending'
			} else {
				sortColumn.value = header
				sortDirection.value = 'ascending'
			}

			displayedRowsCount.value = props.infiniteScrollThreshold

			emit('sort', {
				column: header,
				direction: sortDirection.value,
				sortedData: [],
			})
		}

		const getSortIcon = (header: string) => {
			if (!isColumnSortable(header)) {
				return ''
			}

			if (sortColumn.value !== header) {
				return 'SVGSwitchVertical'
			}

			return sortDirection.value === 'ascending' ? 'SVGArrowUp' : 'SVGArrowDown'
		}

		const loadMoreRows = () => {
			if (loadingMoreRows.value || !hasMoreRows.value) return

			loadingMoreRows.value = true

			setTimeout(() => {
				const totalRows = props.tableRows ? sortedTableRows.value.length : sortedSlotData.value.length

				displayedRowsCount.value = Math.min(
					displayedRowsCount.value + props.infiniteScrollThreshold,
					totalRows
				)
				loadingMoreRows.value = false
			}, DELAY.ONE_HUNDRED_MILLISECONDS)
		}

		const setupIntersectionObserver = () => {
			if (!shouldShowInfiniteScroll.value || intersectionObserver.value) return

			intersectionObserver.value = new IntersectionObserver(
				entries => {
					if (entries[0].isIntersecting && hasMoreRows.value) {
						loadMoreRows()
					}
				},
				{ rootMargin: '100px' }
			)
		}

		const observeSentinel = () => {
			if (intersectionObserver.value && sentinelRef.value) {
				intersectionObserver.value.observe(sentinelRef.value)
			}
		}

		const cleanupObserver = () => {
			if (intersectionObserver.value) {
				intersectionObserver.value.disconnect()
				intersectionObserver.value = null
			}
		}

		watch(
			() => [
				props.tableRows,
				props.sortableData,
			],
			() => {
				displayedRowsCount.value = props.infiniteScrollThreshold
				cleanupObserver()
				nextTick(() => {
					setupIntersectionObserver()
					nextTick(observeSentinel)
				})
			},
			{ deep: true }
		)

		watch(
			[
				sortColumn,
				sortDirection,
			],
			() => {
				displayedRowsCount.value = props.infiniteScrollThreshold
				cleanupObserver()
				nextTick(() => {
					setupIntersectionObserver()
					nextTick(observeSentinel)
				})
			}
		)

		onMounted(() => {
			nextTick(() => {
				setupIntersectionObserver()
				nextTick(observeSentinel)
			})
		})

		onUnmounted(() => {
			cleanupObserver()
		})

		const slotProps = computed(() => ({
			displayedRows: displayedSlotRows.value,
			allRows: sortedSlotData.value,
			hasMore: hasMoreRows.value,
			isLoading: loadingMoreRows.value,
			sortColumn: sortColumn.value,
			sortDirection: sortDirection.value,
		}))

		return () =>
			<div
				class={`ca-table ${props.isStickyHeader ? 'ca-table__sticky-header' : ''}`}
				data-test="ca-table"
			>
				{slots.customTableHeader?.()}

				<div class="ca-table__table-wrapper">
					<div class="ca-table__table-placement">
						<div class="ca-table__table-display">
							<div class="ca-table__table-border">
								<table class="ca-table__table">
									{slots.tableHeader?.()}

									{props.headers &&
										<thead class="ca-table__table-header">
											<tr>
												{props.headers.map((header, headerIndex) => {
													if (!computedExcludeList.value.includes(header.toLowerCase())) {
														return (
															<th
																key={headerIndex}
																class={isColumnSortable(header) ? 'ca-table__sortable-header' : ''}
																scope="col"
																style={isColumnSortable(header) ? { cursor: 'pointer', userSelect: 'none' } : {}}
																onClick={() => handleSort(header)}
															>
																<span>{header}</span>
																{isColumnSortable(header) &&
																	<CAIconComponent icon={getSortIcon(header)} />
																}
															</th>
														)
													}

													return null
												})}
											</tr>
										</thead>
									}

									{slots.tableRows?.() &&
										<tbody class="ca-table__table-body" data-test="ca-table-body">
											{slots.tableRows?.(slotProps.value)}

											{shouldShowInfiniteScroll.value &&
												<tr>
													<td
														colspan={props.headers?.filter(header =>
															!computedExcludeList.value.includes(header.toLowerCase())
														).length || 1}
														style={{ textAlign: 'center', padding: '20px' }}
													>
														{hasMoreRows.value &&
															<div
																ref={sentinelRef}
																style={{ minHeight: '1px' }}
															>
																{loadingMoreRows.value ?
																	<LoadingIcon size="small" />

																	:

																	<span style={{ opacity: 0 }}>Load trigger</span>
																}
															</div>
														}
													</td>
												</tr>
											}
										</tbody>
									}

									{props.tableRows &&
										<tbody class="ca-table__table-body" data-test="ca-table-body">
											{displayedTableRows.value.map((row, index) =>
												<CATableRow key={index}>
													{props.headers?.map(header =>
														!computedExcludeList.value.includes(header.toLowerCase()) ?
															<CATableCell key={header}>
																<span>{row[header]}</span>
															</CATableCell>

															:

															null
													)}
												</CATableRow>
											)}

											{shouldShowInfiniteScroll.value &&
												<tr>
													<td
														colspan={props.headers?.filter(header =>
															!computedExcludeList.value.includes(header.toLowerCase())
														).length || 1}
														style={{ textAlign: 'center', padding: '20px' }}
													>
														{hasMoreRows.value &&
															<div
																ref={sentinelRef}
																style={{ minHeight: '1px' }}
															>
																{loadingMoreRows.value ?
																	<LoadingIcon size="small" />

																	:

																	<span style={{ opacity: 0 }}>Load trigger</span>
																}
															</div>
														}
													</td>
												</tr>
											}
										</tbody>
									}

									{slots.tableFooter?.()}
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
	},
})
